import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";
import { resolveAssetUrl } from "../utils/assetUrl";

const CURRENCY_OPTIONS = [
  { value: "GMD", label: "Dalasi (GMD)", symbol: "D", badgeClass: "bg-emerald-100 text-emerald-700" },
  { value: "USD", label: "Dollar (USD)", symbol: "$", badgeClass: "bg-sky-100 text-sky-700" }
];

const getCurrencyMeta = (currency) => {
  const normalized = String(currency || "GMD").toUpperCase();
  return CURRENCY_OPTIONS.find((item) => item.value === normalized) || CURRENCY_OPTIONS[0];
};

const Payments = () => {
  const { toast, user } = useStore();
  const receiverName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.email || 'Current User';

  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [assets, setAssets] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [dueRentals, setDueRentals] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const rentCurrency = "GMD";
  const rentCurrencyMeta = getCurrencyMeta(rentCurrency);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({
    tenantId: "",
    tenantName: "",
    tenantPhone: "",
    assetId: "",
    assetName: "",
    location: "",
    rentalId: "",
    unitNumber: "",
    rentalType: "",
    rentalCost: "",
    billingPeriod: "",
    currency: "GMD",
    amount: "",
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: "Cash",
    receivedBy: receiverName,
    notes: "",
    contractStartDate: "",
    contractEndDate: "",
    rentBalance: ""
  });

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/payments');
      setPayments(res.data.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast('Failed to load payments', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await api.get('/assets');
      setAssets(res.data.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, []);

  const fetchRentals = useCallback(async () => {
    try {
      const res = await api.get('/rentals');
      setRentals(res.data.data || []);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    }
  }, []);

  const fetchDueRentals = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/due-payments');
      setDueRentals(res.data.data || []);
    } catch (error) {
      console.error('Error fetching due rentals:', error);
    }
  }, []);

  const fetchCompanySettings = useCallback(async () => {
    try {
      const res = await api.get('/companies/my-company');
      setCompanySettings(res.data?.data || null);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchTenants();
    fetchAssets();
    fetchRentals();
    fetchDueRentals();
    fetchCompanySettings();
  }, [fetchPayments, fetchTenants, fetchAssets, fetchRentals, fetchDueRentals, fetchCompanySettings]);

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;

    if (!tenantId) {
      setForm({ ...form, tenantId: "", tenantName: "", tenantPhone: "" });
      return;
    }

    const tenant = tenants.find(t => t.id === parseInt(tenantId));

    if (tenant) {
      setForm({
        ...form,
        tenantId: tenantId,
        tenantName: tenant.tenantName || "",
        tenantPhone: tenant.phone || ""
      });
    }
  };

  const handleAssetChange = (e) => {
    const assetId = e.target.value;

    if (!assetId) {
      setForm({ ...form, assetId: "", assetName: "", location: "" });
      return;
    }

    const asset = assets.find(a => a.id === parseInt(assetId));

    if (asset) {
      setForm({
        ...form,
        assetId: assetId,
        assetName: asset.assetName || "",
        location: asset.location || ""
      });
    }
  };

  const handleRentalChange = async (e) => {
    const rentalId = e.target.value;

    if (!rentalId) {
      setForm({
        ...form,
        tenantId: "",
        tenantName: "",
        tenantPhone: "",
        assetId: "",
        assetName: "",
        location: "",
        rentalId: "",
        unitNumber: "",
        rentalType: "",
        rentalCost: "",
        billingPeriod: "",
        contractStartDate: "",
        contractEndDate: "",
        rentBalance: "",
        amount: ""
      });
      return;
    }

    try {
      const rental = rentals.find(r => r.id === parseInt(rentalId));

      if (rental) {
        let totalPaid = 0;
        try {
          const paymentsRes = await api.get('/payments');
          const allPayments = paymentsRes.data.data || [];
          const contractStart = rental.startDate ? new Date(rental.startDate) : null;
          const rentalPayments = allPayments.filter(p => {
            if (p.rentalId !== parseInt(rentalId)) return false;
            if (String(p.currency || "GMD").toUpperCase() !== rentCurrency) return false;
            if (!contractStart) return true;
            if (!p.paymentDate) return true;
            return new Date(p.paymentDate) >= contractStart;
          });
          totalPaid = rentalPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        } catch (err) {
          console.error('Error calculating total paid:', err);
        }

        const rentalCost = parseFloat(rental.rentalCost || 0);
        const pendingBalance = rentalCost - totalPaid;
        const tenant = tenants.find((item) => String(item.id) === String(rental.tenantId));
        const asset = assets.find((item) => String(item.id) === String(rental.assetId));

        setForm({
          ...form,
          tenantId: rental.tenantId ? String(rental.tenantId) : "",
          tenantName: tenant?.tenantName || rental.tenantName || "",
          tenantPhone: tenant?.phone || "",
          assetId: rental.assetId ? String(rental.assetId) : "",
          assetName: asset?.assetName || rental.assetName || "",
          location: asset?.location || rental.location || "",
          rentalId: rentalId,
          unitNumber: rental.unitNumber || "",
          rentalType: rental.rentalType || "",
          rentalCost: rental.rentalCost?.toString() || "",
          billingPeriod: rental.billingPeriod || "",
          currency: rentCurrency,
          contractStartDate: rental.startDate ? rental.startDate.split('T')[0] : "",
          contractEndDate: rental.endDate ? rental.endDate.split('T')[0] : "",
          rentBalance: pendingBalance > 0 ? pendingBalance.toFixed(2) : "",
          amount: pendingBalance > 0 ? pendingBalance.toFixed(2) : ""
        });
      }
    } catch (error) {
      console.error('Error loading rental details:', error);
      toast('Failed to load rental details', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "currency") {
      const sameCurrency = value === rentCurrency;
      setForm({
        ...form,
        currency: value,
        amount: sameCurrency ? form.rentBalance : ""
      });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const clearForm = () => {
    setForm({
      tenantId: "",
      tenantName: "",
      tenantPhone: "",
      assetId: "",
      assetName: "",
      location: "",
      rentalId: "",
      unitNumber: "",
      rentalType: "",
      rentalCost: "",
      billingPeriod: "",
      currency: rentCurrency,
      amount: "",
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: "Cash",
      receivedBy: receiverName,
      notes: "",
      contractStartDate: "",
      contractEndDate: "",
      rentBalance: ""
    });
    setSelectedPayment(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm({
      tenantId: "",
      tenantName: "",
      tenantPhone: "",
      assetId: "",
      assetName: "",
      location: "",
      rentalId: "",
      unitNumber: "",
      rentalType: "",
      rentalCost: "",
      billingPeriod: "",
      currency: rentCurrency,
      amount: "",
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: "Cash",
      receivedBy: receiverName,
      notes: "",
      contractStartDate: "",
      contractEndDate: "",
      rentBalance: ""
    });
    setSelectedPayment(null);
    setFormOpen(true);
  };

  const saveContractDates = async () => {
    if (!form.rentalId || (!form.contractStartDate && !form.contractEndDate)) return true;

    try {
      await api.put(`/rentals/${form.rentalId}`, {
        datesOnly: true,
        startDate: form.contractStartDate || null,
        endDate: form.contractEndDate || null
      });
      return true;
    } catch (err) {
      console.error('Error updating rental dates:', err);
      return false;
    }
  };

  const handleAdd = async () => {
    if (!form.rentalId) {
      toast('Please select a rental unit', 'error');
      return;
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast('Please enter a valid payment amount', 'error');
      return;
    }

    if (!form.paymentDate) {
      toast('Please select a payment date', 'error');
      return;
    }

    if (form.contractStartDate && form.contractEndDate) {
      const startDate = new Date(form.contractStartDate);
      const endDate = new Date(form.contractEndDate);

      if (endDate < startDate) {
        toast('Contract end date cannot be before start date', 'error');
        return;
      }
    }

    try {
      setLoading(true);

      const datesSaved = await saveContractDates();

      const payload = {
        rentalId: parseInt(form.rentalId),
        amount: parseFloat(form.amount),
        currency: form.currency,
        paymentDate: form.paymentDate,
        paymentMethod: form.paymentMethod,
        notes: form.notes?.trim() || null
      };

      const response = await api.post('/payments', payload);
      toast(response.data.message || 'Payment recorded successfully', 'success');
      if (!datesSaved) {
        toast('Payment saved, but the contract dates were not updated', 'error');
      }
      clearForm();
      fetchPayments();
      fetchRentals();
      fetchDueRentals();
    } catch (error) {
      console.error('Error adding payment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to record payment';
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPayment) {
      toast('Please select a payment to update', 'error');
      return;
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast('Please enter a valid payment amount', 'error');
      return;
    }

    if (!form.paymentDate) {
      toast('Please select a payment date', 'error');
      return;
    }

    if (form.contractStartDate && form.contractEndDate) {
      const startDate = new Date(form.contractStartDate);
      const endDate = new Date(form.contractEndDate);

      if (endDate < startDate) {
        toast('Contract end date cannot be before start date', 'error');
        return;
      }
    }

    try {
      setLoading(true);

      const datesSaved = await saveContractDates();

      const payload = {
        amount: parseFloat(form.amount),
        currency: form.currency,
        paymentDate: form.paymentDate,
        paymentMethod: form.paymentMethod,
        notes: form.notes?.trim() || null
      };

      const response = await api.put(`/payments/${selectedPayment.id}`, payload);
      toast(response.data.message || 'Payment updated successfully', 'success');
      if (!datesSaved) {
        toast('Payment saved, but the contract dates were not updated', 'error');
      }
      clearForm();
      fetchPayments();
      fetchRentals();
      fetchDueRentals();
    } catch (error) {
      console.error('Error updating payment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update payment';
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      const response = await api.delete(`/payments/${deleteTarget.id}`);
      toast(response.data.message || 'Payment deleted successfully', 'success');
      setDeleteTarget(null);
      fetchPayments();
      fetchDueRentals();
    } catch (error) {
      console.error('Error deleting payment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete payment';
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async (payment) => {
    setSelectedPayment(payment);
    setFormOpen(true);

    if (payment.rentalId) {
      try {
        const rentalRes = await api.get(`/rentals/${payment.rentalId}`);
        const rental = rentalRes.data.data;

        const tenant = tenants.find(t => t.id === rental.tenantId);
        const asset = assets.find(a => a.id === rental.assetId);

        setForm({
          tenantId: rental.tenantId?.toString() || "",
          tenantName: tenant?.tenantName || payment.tenantName || "",
          tenantPhone: tenant?.phone || payment.tenantPhone || "",
          assetId: rental.assetId?.toString() || "",
          assetName: asset?.assetName || payment.assetName || "",
          location: asset?.location || payment.location || "",
          rentalId: payment.rentalId?.toString() || "",
          unitNumber: payment.unitNumber || rental.unitNumber || "",
          rentalType: rental.rentalType || "",
          rentalCost: payment.rentalCost?.toString() || rental.rentalCost?.toString() || "",
          billingPeriod: payment.billingPeriod || rental.billingPeriod || "",
          currency: payment.currency || "GMD",
          amount: payment.amount?.toString() || "",
          paymentDate: payment.paymentDate ? payment.paymentDate.split('T')[0] : "",
          paymentMethod: payment.paymentMethod || "Cash",
          receivedBy: payment.receivedBy || receiverName,
          notes: payment.notes || "",
          rentBalance: Number(payment.pendingBalance) > 0 ? Number(payment.pendingBalance).toFixed(2) : "",
          contractStartDate: rental.startDate ? rental.startDate.split('T')[0] : "",
          contractEndDate: rental.endDate ? rental.endDate.split('T')[0] : ""
        });
      } catch (err) {
        console.error('Error loading payment details:', err);
      }
    }
  };

  const receiptNumberFor = (payment) => {
    if (payment.receiptNo) return payment.receiptNo;
    const datePart = String(payment.paymentDate || "").slice(0, 10).replace(/-/g, "");
    if (!payment.id || !datePart) return "";
    return `${datePart}-${String(payment.id).padStart(6, "0")}`;
  };

  const numberToWords = (num, currency) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convert = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 1000000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      return convert(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + convert(n % 1000000) : '');
    };

    const major = Math.floor(Math.abs(Number(num) || 0) + 1e-8);
    const dollar = currency === "USD";
    const majorName = dollar ? (major === 1 ? "Dollar" : "Dollars") : (major === 1 ? "Dalasi" : "Dalasis");

    if (major === 0) return `Zero ${dollar ? "Dollars" : "Dalasi"} Only`;
    return `${convert(major)} ${majorName} Only`;
  };

  const handlePrintReceipt = (payment) => {
    const receiptWindow = window.open('', '_blank');
    if (!receiptWindow) {
      toast("Please allow pop-ups to print the receipt", "error");
      return;
    }

    const esc = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const companyName = companySettings?.companyName || user?.companyName || user?.company || user?.name || 'PROPERTY MANAGEMENT COMPANY';
    const companyAddress = companySettings?.address || user?.address || user?.companyAddress || '';
    const companyPhone = companySettings?.phone || user?.phone || user?.companyPhone || '';
    const companyEmail = companySettings?.email || user?.email || '';
    const companyLogo = resolveAssetUrl(companySettings?.logo || user?.logo || user?.companyLogo || null);

    const contactLine = [
      companyAddress,
      companyPhone ? `Tel: ${companyPhone}` : '',
      companyEmail ? `Email: ${companyEmail}` : ''
    ].filter(Boolean).join(' | ');

    const receiptNo = receiptNumberFor(payment);
    const receiptDateMatch = String(payment.paymentDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    const receiptDate = receiptDateMatch
      ? `${receiptDateMatch[3]}/${receiptDateMatch[2]}/${receiptDateMatch[1]}`
      : "";

    const snapshotted = payment.receiptRentalCost !== null && payment.receiptRentalCost !== undefined && payment.receiptRentalCost !== "";
    const tenantName = snapshotted ? payment.receiptTenantName : payment.tenantName;
    const tenantPhone = snapshotted ? payment.receiptTenantPhone : payment.tenantPhone;
    const assetName = snapshotted ? payment.receiptAssetName : payment.assetName;
    const location = snapshotted ? payment.receiptLocation : payment.location;
    const unitNumber = snapshotted ? payment.receiptUnitNumber : payment.unitNumber;
    const rentalType = snapshotted ? payment.receiptRentalType : payment.rentalType;
    const billingPeriod = snapshotted ? payment.receiptBillingPeriod : payment.billingPeriod;
    const contractStartDate = snapshotted ? payment.receiptContractStart : payment.contractStartDate;
    const contractEndDate = snapshotted ? payment.receiptContractEnd : payment.contractEndDate;

    const amountPaid = parseFloat(payment.amount || 0);
    const balanceAfterPayment = Math.max(parseFloat(payment.balanceAfterPayment ?? payment.pendingBalance ?? 0) || 0, 0);
    const rentalCost = parseFloat((snapshotted ? payment.receiptRentalCost : payment.rentalCost) || 0);
    const paymentCurrencyMeta = getCurrencyMeta(payment.currency);
    const balanceCurrencyMeta = getCurrencyMeta(payment.rentCurrency || rentCurrency);
    const paymentCurrency = paymentCurrencyMeta.value;
    const formatAmount = (value, symbol) =>
      `${symbol}${Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;

    const formatPeriodDate = (dateStr) => {
      const match = String(dateStr || "").match(/^(\d{4})-(\d{2})/);
      if (!match) return null;
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
      const monthIndex = Number(match[2]) - 1;
      if (monthIndex < 0 || monthIndex > 11) return null;
      return `${months[monthIndex]} ${match[1]}`;
    };

    const periodFrom = formatPeriodDate(contractStartDate);
    const periodTo = formatPeriodDate(contractEndDate);
    const contractPeriod = periodFrom && periodTo ? `${periodFrom} to ${periodTo}` : (periodFrom || periodTo || "");
    const propertyLine = `${assetName || "N/A"}${location ? `, ${location}` : ""}`;
    const amountToWords = numberToWords(amountPaid, paymentCurrency);

    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rent Payment Receipt</title>
        <style>
          @page { size: A5 portrait; margin: 6mm 7mm 8mm 7mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html, body {
            width: 148mm;
            min-height: 210mm;
            background: #fff;
            color: #000;
            font-family: Helvetica, Arial, sans-serif;
            font-size: 8pt;
          }
          .receipt { width: 100%; }

          .company-header { text-align: center; }
          .company-logo { height: 12mm; width: auto; max-width: 28mm; object-fit: contain; display: block; margin: 0 auto 2mm; }
          .company-logo-placeholder {
            width: 11mm; height: 11mm; margin: 0 auto 2mm;
            border: 0.6pt solid #c0c0c0; display: flex; align-items: center; justify-content: center;
            font-size: 12pt; font-weight: bold;
          }
          .company-name { font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.2pt; }
          .company-contact { font-size: 8pt; margin-top: 1mm; }
          .header-divider { border: none; border-top: 0.6pt solid #c0c0c0; width: 70%; margin: 3.5mm auto 3.5mm; }

          .receipt-title { text-align: center; font-size: 13pt; font-weight: bold; margin-bottom: 3.5mm; }

          .receipt-meta { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 2.5mm; }
          .receipt-no { font-size: 9pt; font-weight: bold; }
          .receipt-date { font-size: 8pt; }

          .tenant-box {
            border: 0.6pt solid #c0c0c0;
            background: #fafafa;
            padding: 1.6mm 2mm;
            margin-bottom: 2.4mm;
            font-size: 8pt;
            font-weight: bold;
          }

          .details-table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
          .details-table td {
            border: 0.5pt solid #c0c0c0;
            padding: 1.15mm 1.6mm;
            font-size: 8pt;
            background: #fcfcfc;
          }
          .details-table td:first-child { font-weight: bold; width: 44%; text-align: left; }
          .details-table td:last-child { text-align: right; font-weight: normal; }
          .details-table .amount-paid td:last-child { font-weight: bold; font-size: 9pt; }

          .period-line { text-align: center; font-weight: bold; font-size: 8pt; margin: 1.5mm 0 2mm; }
          .amount-words, .notes-line { font-size: 8pt; margin-bottom: 1.6mm; }

          .thank-you { text-align: center; font-style: italic; font-size: 7pt; margin: 8mm 0 3.5mm; }

          .signature-table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
          .signature-table td {
            border: 0.5pt solid #808080;
            width: 50%;
            height: 17mm;
            padding: 2.2mm 1.6mm;
            vertical-align: top;
          }
          .signature-label { font-size: 6pt; }
          .signature-name { font-size: 7pt; margin-top: 4mm; }

          .footer-note { text-align: center; font-size: 5pt; color: #808080; font-style: italic; }

          @media print {
            html, body { width: auto; min-height: auto; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="company-header">
            ${companyLogo
              ? `<img src="${esc(companyLogo)}" class="company-logo" alt="Company Logo" />`
              : `<div class="company-logo-placeholder">${esc(companyName.charAt(0))}</div>`
            }
            <div class="company-name">${esc(companyName)}</div>
            ${contactLine ? `<div class="company-contact">${esc(contactLine)}</div>` : ""}
          </div>

          <hr class="header-divider" />

          <div class="receipt-title">RENT PAYMENT RECEIPT</div>

          <div class="receipt-meta">
            <div class="receipt-no">Receipt No: ${esc(receiptNo)}</div>
            <div class="receipt-date">Date: ${esc(receiptDate)}</div>
          </div>

          <div class="tenant-box">
            Tenant: ${esc(tenantName || "N/A")} | Phone: ${esc(tenantPhone || "N/A")}
          </div>

          <table class="details-table">
            <tr>
              <td>Property</td>
              <td>${esc(propertyLine)}</td>
            </tr>
            <tr>
              <td>Unit/Type</td>
              <td>${esc(`${unitNumber || "N/A"} - ${rentalType || "N/A"}`)}</td>
            </tr>
            <tr>
              <td>Billing Period</td>
              <td>${esc(billingPeriod || "N/A")}</td>
            </tr>
            <tr>
              <td>Rental Cost</td>
              <td>${esc(formatAmount(rentalCost, balanceCurrencyMeta.symbol))}</td>
            </tr>
            <tr class="amount-paid">
              <td>Amount Paid</td>
              <td>${esc(formatAmount(amountPaid, paymentCurrencyMeta.symbol))}</td>
            </tr>
            <tr>
              <td>Payment Method</td>
              <td>${esc(payment.paymentMethod || "Cash")}</td>
            </tr>
            <tr>
              <td>Balance</td>
              <td>${esc(formatAmount(balanceAfterPayment, balanceCurrencyMeta.symbol))}</td>
            </tr>
          </table>

          ${contractPeriod ? `<div class="period-line">Contract Period: ${esc(contractPeriod)}</div>` : ""}
          <div class="amount-words">Amount: ${esc(amountToWords)}.</div>
          ${paymentCurrency !== balanceCurrencyMeta.value ? `<div class="notes-line">Recorded in dollars. This payment does not change the dalasi rent balance.</div>` : ""}
          ${payment.notes ? `<div class="notes-line">Note: ${esc(payment.notes)}</div>` : ""}

          <div class="thank-you">Thank you for your payment!</div>

          <table class="signature-table">
            <tr>
              <td>
                <div class="signature-label">Received By:</div>
                <div class="signature-name">${esc(payment.receivedBy || "")}</div>
              </td>
              <td>
                <div class="signature-label">Client Signature:</div>
                <div class="signature-name">____________</div>
              </td>
            </tr>
          </table>

          <div class="footer-note">Computer-generated receipt. Please keep for your records.</div>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);

    receiptWindow.document.close();
  };

  const filtered = payments.filter(payment =>
    (payment.tenantName || '').toLowerCase().includes(search.toLowerCase()) ||
    (payment.assetName || '').toLowerCase().includes(search.toLowerCase()) ||
    (payment.unitNumber || '').toLowerCase().includes(search.toLowerCase()) ||
    (payment.location || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedCurrencyMeta = getCurrencyMeta(form.currency);

  return (
    <div>
      <PageHeader
        title="Payments"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Payment
          </Button>
        }
      />

      {!!dueRentals.length && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-amber-900">Due Rentals Alert</h3>
            <span className="text-xs font-semibold text-amber-700">{dueRentals.length} rental(s) in due window</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-amber-800">
                  <th className="py-2 pr-3 font-semibold">Tenant</th>
                  <th className="py-2 pr-3 font-semibold">Property</th>
                  <th className="py-2 pr-3 font-semibold">Billing</th>
                  <th className="py-2 pr-3 font-semibold">Contract End</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {dueRentals.slice(0, 6).map((item) => (
                  <tr key={`due-${item.rentalId}`} className="border-t border-amber-100">
                    <td className="py-2 pr-3 text-gray-800 whitespace-nowrap">{item.tenantName || 'Unassigned'}</td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{item.assetName || 'N/A'} - {item.unitNumber || 'N/A'}</td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{item.billingPeriod || '-'}</td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{item.dueDate ? new Date(item.dueDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${item.status === 'Overdue' ? 'bg-red-100 text-red-700' : item.status === 'Due Today' ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-700'}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DataTable
        columns={[
          { key: "tenantName", label: "Tenant Name", render: (row) => row.tenantName || "-" },
          { key: "unitNumber", label: "Rental Unit", render: (row) => row.unitNumber || "-" },
          { key: "assetName", label: "Asset Name", render: (row) => row.assetName || "-" },
          { key: "location", label: "Location", render: (row) => row.location || "-" },
          {
            key: "amount",
            label: "Amount Paid",
            className: "font-semibold text-green-600",
            sortValue: (row) => parseFloat(row.amount || 0),
            render: (row) => {
              const currencyMeta = getCurrencyMeta(row.currency);
              return (
                <span className="inline-flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${currencyMeta.badgeClass}`}>
                    {currencyMeta.symbol}
                  </span>
                  <span>{parseFloat(row.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </span>
              );
            }
          },
          {
            key: "pendingBalance",
            label: "Pending Balance",
            className: "font-semibold text-red-600",
            sortValue: (row) => parseFloat(row.pendingBalance || 0),
            render: (row) => {
              const currencyMeta = getCurrencyMeta(row.rentCurrency || rentCurrency);
              return (
                <span className="inline-flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${currencyMeta.badgeClass}`}>
                    {currencyMeta.symbol}
                  </span>
                  <span>{parseFloat(row.pendingBalance || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </span>
              );
            }
          },
          {
            key: "receiptNo",
            label: "Receipt No",
            render: (row) => receiptNumberFor(row) || "-"
          },
          {
            key: "paymentDate",
            label: "Payment Date",
            sortValue: (row) => new Date(row.paymentDate || 0).getTime(),
            render: (row) => (row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : "-")
          }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by tenant, asset, unit or location..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        extraActions={(payment) => (
          <button
            type="button"
            onClick={() => handlePrintReceipt(payment)}
            className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-md hover:bg-teal-700 transition-colors"
          >
            Print
          </button>
        )}
        emptyMessage='No payments found. Click "Add Payment" to record a payment.'
        defaultSortKey="paymentDate"
        defaultSortDir="desc"
      />

      <Modal
        open={formOpen}
        title={selectedPayment ? "Update Payment" : "Add Payment"}
        onClose={clearForm}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={clearForm} disabled={loading}>
              Cancel
            </Button>
            {selectedPayment ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div>
              <label className={LABEL_CLASS}>Tenant Name</label>
              <select name="tenantId" value={form.tenantId} onChange={handleTenantChange} disabled className={FIELD_CLASS}>
                <option value="">Select tenant</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.tenantName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Asset Name</label>
              <select name="assetId" value={form.assetId} onChange={handleAssetChange} disabled className={FIELD_CLASS}>
                <option value="">Select asset</option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>{asset.assetName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Rental Unit No. <span className="text-red-500">*</span>
              </label>
              <select name="rentalId" value={form.rentalId} onChange={handleRentalChange} disabled={selectedPayment !== null} className={FIELD_CLASS}>
                <option value="">Select rental unit</option>
                {rentals.map((rental) => (
                  <option key={rental.id} value={rental.id}>{rental.unitNumber} - {rental.assetName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Rental Type</label>
              <input type="text" name="rentalType" value={form.rentalType} disabled className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Rental Cost</label>
              <input
                type="text"
                name="rentalCost"
                value={form.rentalCost ? `${rentCurrencyMeta.symbol} ${parseFloat(form.rentalCost).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""}
                disabled
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Note</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Add notes (optional)" className={`${FIELD_CLASS} resize-none`} />
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input type="date" name="paymentDate" value={form.paymentDate} onChange={handleChange} className={FIELD_CLASS} />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className={LABEL_CLASS}>Tenant Phone Number</label>
              <input type="text" name="tenantPhone" value={form.tenantPhone} disabled className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Location</label>
              <input type="text" name="location" value={form.location} disabled className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Billing Period</label>
              <input type="text" name="billingPeriod" value={form.billingPeriod} disabled className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className={FIELD_CLASS}>
                {CURRENCY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.symbol} {item.label}</option>
                ))}
              </select>
              {form.currency !== rentCurrency && (
                <p className="mt-2 text-xs text-amber-700">
                  This dollar payment is kept separate and does not reduce the dalasi rent balance.
                </p>
              )}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Amount Paid <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${selectedCurrencyMeta.badgeClass}`}>
                  {selectedCurrencyMeta.symbol}
                </span>
                <input type="number" name="amount" value={form.amount} onChange={handleChange} step="0.01" min="0" placeholder="Enter amount" className={`flex-1 ${FIELD_CLASS}`} />
              </div>
            </div>
            <div>
              <label className={LABEL_CLASS}>Payment Method</label>
              <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} className={FIELD_CLASS}>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Mobile Money">Mobile Money</option>
                <option value="Cheque">Cheque</option>
                <option value="Card">Card</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Contract Start Date</label>
              <input type="date" name="contractStartDate" value={form.contractStartDate} onChange={handleChange} className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Contract End Date</label>
              <input type="date" name="contractEndDate" value={form.contractEndDate} onChange={handleChange} className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Received By</label>
              <input type="text" name="receivedBy" value={form.receivedBy} disabled placeholder="Auto-filled from your login" className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Receipt No</label>
              <input
                type="text"
                value={selectedPayment ? receiptNumberFor(selectedPayment) : ""}
                disabled
                placeholder="Assigned when the payment is saved"
                className={FIELD_CLASS}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message="Are you sure you want to delete this payment record?"
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Payments;